import { useEffect, useMemo } from "react"
import { Link, useLocation } from "react-router-dom"

import useMenuItems, { pathMatches, type MenuItem } from "@/hooks/useMenuItems"
import { deepRouteLabel } from "@/config/routeLabels"
import { useTenant } from "@/contexts/TenantContext"

export interface Crumb {
  label: string
  link?: string
}

/**
 * Trail de ubicación actual: Inicio > [Sección] > [Item] > [Subitem o detalle].
 *
 * Fuente primaria = jerarquía del menú (useMenuItems: ya viene filtrada por
 * permisos, con nombres del tenant y secciones intercaladas como headers).
 * Fuente secundaria = config/routeLabels.ts para subrutas profundas que el
 * menú no conoce (/empresas/:id, …).
 *
 * El matching reusa pathMatches tal cual — maneja el base path por tenant
 * (/claricel/...) que un startsWith ingenuo rompería.
 */
export function useBreadcrumbTrail(): Crumb[] {
  const items = useMenuItems()
  const { pathname } = useLocation()

  return useMemo(() => {
    let sectionLabel: string | undefined
    let matchedItem: MenuItem | undefined
    let matchedSub: MenuItem | undefined

    let currentSection: string | undefined
    for (const item of items) {
      if (item.isHeader) {
        currentSection = item.label
        continue
      }
      if (item.hasSubItems) {
        const sub = item.subItems.find((s) => pathMatches(s.link, pathname))
        if (sub) {
          sectionLabel = currentSection
          matchedItem = item
          matchedSub = sub
          break
        }
      }
      if (pathMatches(item.link, pathname)) {
        sectionLabel = currentSection
        matchedItem = item
        break
      }
    }

    if (!matchedItem) return []

    const deepLabel = deepRouteLabel(pathname)
    const crumbs: Crumb[] = []

    if (matchedItem.link !== "/inicio") {
      crumbs.push({ label: "Inicio", link: "/inicio" })
    }
    if (sectionLabel) {
      crumbs.push({ label: sectionLabel }) // sección: orientación, no navegable
    }
    crumbs.push({ label: matchedItem.label, link: matchedItem.link })
    if (matchedSub) {
      crumbs.push({ label: matchedSub.label, link: matchedSub.link })
    }
    if (deepLabel) {
      crumbs.push({ label: deepLabel })
    }

    // El último crumb es la página actual: sin link
    const last = crumbs[crumbs.length - 1]
    delete last.link
    return crumbs
  }, [items, pathname])
}

/**
 * Franja de breadcrumbs renderizada UNA sola vez en el Layout (no por página).
 * Cuando hay trail, agrega `has-page-breadcrumbs` al body para que el CSS
 * reduzca el padding-top de .page-content (la franja ya libra el topbar fijo).
 * También mantiene document.title en sintonía con la página actual.
 */
const Breadcrumbs = () => {
  const trail = useBreadcrumbTrail()
  const { tenant, loading: tenantLoading } = useTenant()
  const company = tenant?.company || "CRM"

  useEffect(() => {
    const lastLabel = trail.length > 0 ? trail[trail.length - 1].label : ""
    document.title = lastLabel ? `${lastLabel} | ${company}` : company
    // tenantLoading en deps: TenantContext asigna document.title al terminar su
    // fetch async; re-correr aquí después garantiza que el title de página gane.
  }, [trail, company, tenantLoading])

  useEffect(() => {
    document.body.classList.toggle("has-page-breadcrumbs", trail.length > 0)
    return () => {
      document.body.classList.remove("has-page-breadcrumbs")
    }
  }, [trail.length])

  if (trail.length === 0) return null

  return (
    <div className="app-page-breadcrumbs">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb mb-0">
          {trail.map((crumb, index) => {
            const isLast = index === trail.length - 1
            return (
              <li
                key={`${crumb.label}-${index}`}
                className={`breadcrumb-item${isLast ? " active" : ""}`}
                aria-current={isLast ? "page" : undefined}
              >
                {crumb.link && !isLast ? (
                  <Link to={crumb.link}>{crumb.label}</Link>
                ) : (
                  crumb.label
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    </div>
  )
}

export default Breadcrumbs
